import moment from 'moment';

import { memoize } from '@philips-emr/tws-utils';

const CONSTANTS = {
  DAY: 'day',
  HOUR: 'hour',
  MONTH: 'month',
  WEEK: 'week',
  MINUTE: 'minute',
};

function getWeekPeriod(date, method = 'startOf') {
  return moment.utc(date)[method](CONSTANTS.WEEK);
}

function getNormalizedHour(hour) {
  return moment
    .utc()
    .minute(0)
    .hour(hour);
}

function getDay(date, events = [], active = true, inRange = false) {
  return {
    active,
    date: moment.utc(date),
    events: getDayEvents(date, events),
    inRange,
  };
}

function getDayEvents(date, events = []) {
  if (!Array.isArray(events)) return [];

  return events
    .map((event) => ({ ...event, date: moment.utc(event.date) }))
    .filter((event) => event.date.isSame(date, 'date'));
}

function getHourEvents(date, events = []) {
  const today = moment.utc();

  function incrementEvent(event) {
    const start = moment.utc(event.date);
    const end = event.end ? moment.utc(event.end) : moment.utc(start).add(30, CONSTANTS.MINUTE);

    const duration = end.diff(start, 'minute');
    const past = start.isBefore(today, 'date');

    return {
      ...event,
      date: start,
      duration,
      end,
      past,
    };
  }

  return events
    .map(incrementEvent)
    .filter((event) => event.date.hour() === date.hour());
}

const toMoment = ({ startDate, endDate }) => ({
  startDate: moment.utc(startDate),
  endDate: moment.utc(endDate),
});

const byInitialDate = (aRange, bRange) => (
  !aRange.startDate.isSame(bRange.startDate)
    ? aRange.startDate.isBefore(bRange.startDate) ? -1 : 1
    : 0
);

export const optimizeRanges = memoize((ogRanges) => {
  if (!ogRanges.length) {
    return [];
  }

  const [initRange, ...ranges] = ogRanges.map(toMoment).sort(byInitialDate);
  const optimizedRanges = [initRange];

  for (const range of ranges) {
    const { startDate: currentStartDate, endDate: currentEndDate } = range;

    const lastRange = optimizedRanges[optimizedRanges.length - 1];
    const { endDate: lastEndDate } = lastRange;

    if (currentEndDate.isSameOrBefore(lastEndDate)) {
      continue;
    } else if (currentStartDate.isSameOrBefore(lastEndDate)) {
      lastRange.endDate = currentEndDate;
    } else {
      optimizedRanges.push(range);
    }
  }

  return optimizedRanges;
});

const getRangeMap = memoize((ranges, gridLimits) =>  {
  const monthRanges = getRangesWithinLimits(ranges, gridLimits);
  const rangeMap = new Map();

  for (const range of monthRanges) {
    const ctrlDate = moment.utc(range.startDate);

    while (ctrlDate.isSameOrBefore(range.endDate)) {
      rangeMap.set(ctrlDate.dayOfYear(), null);
      ctrlDate.add(1, CONSTANTS.DAY);
    }
  }

  return rangeMap;
});

function getRangesWithinLimits(ranges, gridLimits) {
  const { gridStart, gridEnd } = gridLimits;

  const isWithinMonth = range => (
    range.startDate.isSameOrBefore(gridEnd) && range.endDate.isSameOrAfter(gridStart)
  );

  const cropLimits = range => {
    const { startDate, endDate } = range;

    if (startDate.isBefore(gridStart)) {
      return { startDate: gridStart, endDate }
    } else if (endDate.isAfter(gridEnd)) {
      return { startDate, endDate: gridEnd };
    }

    return range;
  }

  return ranges.filter(isWithinMonth).map(cropLimits);
}

function getGridLimits(date) {
  const monthStart = moment.utc(date).startOf(CONSTANTS.MONTH);
  const monthEnd = moment.utc(date).endOf(CONSTANTS.MONTH);
  const gridStart = moment.utc(monthStart).startOf(CONSTANTS.WEEK);
  const gridEnd = moment.utc(monthEnd).endOf(CONSTANTS.WEEK);

  const gridLimits = {
    gridEnd,
    gridStart,
    monthEnd,
    monthStart,
  };

  return gridEnd.diff(gridStart, CONSTANTS.WEEK) < 5
    ? { ...gridLimits, gridEnd: gridEnd.add(1, CONSTANTS.WEEK) }
    : gridLimits;
}

function buildMonthGridFromLimits(gridLimits, events, ranges) {
  const { gridStart, gridEnd, monthStart } = gridLimits;

  const gridDays = gridEnd.diff(gridStart, CONSTANTS.DAY);
  const date = moment.utc(gridStart);
  const weeks = gridDays / 7;
  const rangeMap = getRangeMap(ranges, gridLimits);
  const grid = [];

  for (let w = 0; w < weeks; w++) {
    const week = [];

    for (let i = 0; i < 7; i++) {
      const active = date.isSame(monthStart, CONSTANTS.MONTH);
      const inRange = ranges.length === 0 || rangeMap.has(date.dayOfYear());

      const day = getDay(date, events, active, inRange);

      week.push(day);
      date.add(1, CONSTANTS.DAY);
    }

    grid.push(week);
  }

  return grid;
}

export function getMonthGrid(date, events, ranges = []) {
  const startOfMonth = moment.utc(date).startOf(CONSTANTS.MONTH);
  const optmizedRanges = optimizeRanges(ranges);

  const gridLimits = getGridLimits(startOfMonth);
  const grid = buildMonthGridFromLimits(gridLimits, events, optmizedRanges);

  return grid;
}

export function getWeekGrid(date, startOfDay, endOfDay, events) {
  const hours = getHours(startOfDay, endOfDay);
  const days = getNormalizedWeekDays(date, startOfDay, endOfDay, events);

  return [[...hours], ...days];
}

function getNormalizedWeekDays(date, startOfDay, endOfDay, events) {
  const days = [];
  const [weekStart, weekEnd] = [
    getWeekPeriod(date, 'startOf'),
    getWeekPeriod(date, 'endOf'),
  ];

  while (weekStart.isSameOrBefore(weekEnd, CONSTANTS.WEEK)) {
    const [startHour, endHour] = [
      getNormalizedHour(startOfDay),
      getNormalizedHour(endOfDay),
    ];

    const day = [];
    const dayEvents = getDayEvents(weekStart, events);

    while (startHour.isSameOrBefore(endHour, CONSTANTS.HOUR)) {
      const hour = {
        events: getHourEvents(startHour, dayEvents),
      };

      day.push(hour);
      startHour.add(1, CONSTANTS.HOUR);
    }

    days.push(day);
    weekStart.add(1, CONSTANTS.DAY);
  }

  return days;
}

function getHours(startOfDay, endOfDay) {
  const hours = [];
  const [startHour, endHour] = [
    getNormalizedHour(startOfDay),
    getNormalizedHour(endOfDay),
  ];

  while (startHour.isSameOrBefore(endHour, CONSTANTS.HOUR)) {
    const text = startHour.format('hh:mma');
    const shortText = startHour.format('ha');

    hours.push({ text, shortText });

    startHour.add(1, CONSTANTS.HOUR);
  }

  return hours;
}

export function isDateWithinRange(date, range) {
  if (!range) {
    return true;
  }

  const [minDate, maxDate] = range;

  const meetsMinLimit = !minDate || date.isSameOrAfter(minDate, 'day');
  const meetsMaxLimit = !maxDate || date.isSameOrBefore(maxDate, 'day');

  return meetsMinLimit && meetsMaxLimit;
}

export function getWeekDays(short = false) {
  return short ?
    moment.weekdaysShort() :
    moment.weekdays();
}

export function getMonthEvents(events = [], date = moment().utc()) {
  return events
    .map((event) => ({ ...event, date: moment.utc(event.date) }))
    .filter((event) => event.date.isSame(date, 'month'));
}

export const getMonthEventsGroupedByDay = memoize((events = [], date = moment().utc()) =>  {
  return getMonthEvents(events, date).reduce((groupedEvents, event) => {
    const eventDay = event.date.format('d');
    if (!Object.prototype.hasOwnProperty.call(groupedEvents, eventDay)) {
      groupedEvents[eventDay] = [];
    }

    groupedEvents[eventDay].push(event);
    return groupedEvents;
  }, []);
});

export function getDateEvents(events = [], date = moment().utc()) {
  return events
    .map((event) => ({ ...event, date: moment.utc(event.date) }))
    .filter((event) => date.isSame(event.date, 'date'));
}
